/**
 * Display formatting for Alpha Atlas report values.
 *
 * Returns, edges, intervals, MFE/MAE, and costs are stored as percent-micros: the materializer
 * multiplies a percent return by 1,000,000 (`return_percent_micros` in the forward-outcome runtime),
 * so 1% is 1,000,000 micros and a round-trip cost of 10 basis points is 100,000 micros.
 */
import { CANDLESTICK_PATTERN_ID_MAP } from '@einhaus/project314-shared';
import type { AlphaAtlasConditionKind } from './AlphaAtlasReportState';

export const PERCENT_MICROS_PER_PERCENT = 1_000_000;

export type ValueTone = 'positive' | 'negative' | 'neutral';

const DURATION_SEGMENT = /^\d+(?:m|h|d|w|mo)$/i;
const NUMERIC_PARAMETER = /^[\d.-]+$/;
const ACRONYMS = new Set(['adx', 'atm', 'atr', 'bb', 'dte', 'ema', 'macd', 'obv', 'rsi', 'sma', 'spy', 'vwap']);
const ACRONYM_WITH_PERIOD = /^(adx|atr|bb|ema|macd|obv|rsi|sma|vwap)(\d+)$/i;
const CATALOG_VALUE_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
    candlestick: CANDLESTICK_PATTERN_ID_MAP,
    currentCandleColor: {
        g: 'Green',
        r: 'Red'
    }
};

export const percentFromMicros = (micros: number): number => micros / PERCENT_MICROS_PER_PERCENT;

/** "1.93%", or "+1.93%" / "-1.93%" when `signed`; null renders as an em dash. */
export const formatPercentMicros = (micros: number | null, options?: { signed?: boolean; digits?: number }): string => {
    if (micros === null) return '—';
    const digits = options?.digits ?? 2;
    const rounded = Number(percentFromMicros(micros).toFixed(digits));
    const text = `${Math.abs(rounded).toFixed(digits)}%`;
    if (rounded < 0) return `-${text}`;
    return options?.signed && rounded > 0 ? `+${text}` : text;
};

/** Ratio in [0, 1] rendered as a percentage, e.g. win rate 0.56 → "56.0%". */
export const formatRatio = (value: number | null, digits = 1): string => (value === null ? '—' : `${(value * 100).toFixed(digits)}%`);

export const formatCount = (value: number): string => value.toLocaleString('en-US');

/** Horizon seconds as a compact label: 86,400 → "1d", 604,800 → "1w", 1,800 → "30m". */
export const formatHorizon = (seconds: number): string => {
    if (seconds % 86_400 === 0) {
        const days = seconds / 86_400;
        return days >= 7 && days % 7 === 0 ? `${days / 7}w` : `${days}d`;
    }
    if (seconds % 3_600 === 0) return `${seconds / 3_600}h`;
    if (seconds % 60 === 0) return `${seconds / 60}m`;
    return `${seconds}s`;
};

const marketTimeFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' });
const marketDateFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });

export const formatMarketDateTime = (unix: number | null): string =>
    unix === null ? 'Unavailable' : marketTimeFormatter.format(unix * 1_000);
export const formatMarketDate = (unix: number | null): string => (unix === null ? 'Unavailable' : marketDateFormatter.format(unix * 1_000));

export const toneForMicros = (micros: number | null): ValueTone => {
    if (micros === null || micros === 0) return 'neutral';
    return micros > 0 ? 'positive' : 'negative';
};

export const TONE_TEXT_CLASS: Record<ValueTone, string> = {
    negative: 'text-red-400',
    neutral: 'text-zinc-300',
    positive: 'text-green-400'
};

const splitWords = (value: string): string[] =>
    value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .split(/[\s_-]+/)
        .filter(Boolean);

const formatWord = (word: string): string => {
    const lower = word.toLowerCase();
    if (ACRONYMS.has(lower)) return lower.toUpperCase();
    const acronymWithPeriod = lower.match(ACRONYM_WITH_PERIOD);
    const acronym = acronymWithPeriod?.[1];
    const period = acronymWithPeriod?.[2];
    if (acronym && period) return `${acronym.toUpperCase()} ${period}`;
    return NUMERIC_PARAMETER.test(word) ? word : lower;
};

const sentence = (words: string[]): string => {
    const text = words.map(formatWord).join(' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatCatalogValue = (feature: string, value: string): string => {
    const mappedValue = CATALOG_VALUE_LABELS[feature]?.[value];
    if (mappedValue) return mappedValue;
    return NUMERIC_PARAMETER.test(value) ? value : splitWords(value).map(formatWord).join(' ');
};

/**
 * Turns a catalog semantic key such as `macdZone.1d.12-26-9.bullish` into "MACD zone: bullish (12-26-9)"
 * and `rsiExitedOversold.1d` into "RSI exited oversold". Duration segments are dropped because the
 * product already fixes them; the raw key stays available for tooltips.
 */
export const humanizeCatalogLabel = (label: string | null): string => {
    if (!label) return '';
    const [feature, ...rest] = label.split('.').filter(Boolean);
    if (!feature) return label;
    const parameters = rest.filter((segment) => !DURATION_SEGMENT.test(segment));
    const featureText = sentence(splitWords(feature));
    if (parameters.length === 0) return featureText;
    const value = parameters[parameters.length - 1] ?? '';
    const qualifiers = parameters
        .slice(0, -1)
        .map((qualifier) => (NUMERIC_PARAMETER.test(qualifier) ? qualifier : splitWords(qualifier).map(formatWord).join(' ')));
    const valueText = formatCatalogValue(feature, value);
    return `${featureText}: ${valueText}${qualifiers.length > 0 ? ` (${qualifiers.join(', ')})` : ''}`;
};

/** "RSI exited oversold + MACD zone: bullish (12-26-9)", or the single trigger/context name for one-sided rows. */
export const describeCombination = (row: {
    conditionKind: AlphaAtlasConditionKind;
    parentLabel: string | null;
    childLabel: string | null;
}): string => {
    const parent = humanizeCatalogLabel(row.parentLabel);
    const child = humanizeCatalogLabel(row.childLabel);
    if (row.conditionKind === 'parentAndChild') return parent && child ? `${parent} + ${child}` : parent || child || 'Trigger + context';
    if (row.conditionKind === 'parentOnly') return parent || 'Trigger only';
    return child || 'Context only';
};
